import { Circle, DeleteButton, Heading, Tooltip } from '@mm/components';
import { useResolutionInfo, useDebounce } from '@mm/hooks';
import {
  AngleDownLightIcon,
  CloseCircleIcon,
  FilterFilledIcon,
  FilterIcon,
  QuestionCircleIcon,
  SearchIcon,
  ExclamationIcon,
} from '@mm/icons';
import {
  ConversationsSearchResultMatch,
  useLiveChatCRUDActions,
} from '@mm/state/actions/LiveChatCRUDActions';
import JSONUtils from '@mm/state/helpers/json-utils';
import {
  identifyChannel,
  preferredConversationName,
} from '@mm/state/helpers/live-chat-helpers';
import {
  LiveChatChannel,
  LivechatConversationModel,
  LiveChatInboxStatus,
} from '@mm/state/model/LivechatConversationModel';
import {
  LivechatConversationStateOption,
  LivechatConversationStateOptions,
} from '@mm/state/model/LivechatConversationStateModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { defaultPageIdSelector } from '@mm/state/selectors/pageSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { colors } from '@mm/utils';
import { useActor, useSelector as useXstateSelector } from '@xstate/react';
import { ReactComponent as InstagramIcon } from 'assets/instagram.svg';
import { ReactComponent as FBIcon } from 'assets/messenger-fb-new.svg';
import { ReactComponent as SMSIcon } from 'assets/smartphone.svg';
import { ReactComponent as WebchatIcon } from 'assets/webchat-new.svg';
import classnames from 'classnames';
import { isEqual } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  InfiniteLoader,
  List,
} from 'react-virtualized';
import { useContextSelector } from 'use-context-selector';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import PrimarySelect from '../../../../../common/components/PrimarySelect/PrimarySelect';
import { TypedPrimarySelect } from '../../../../../common/components/PrimarySelect/TypedPrimarySelect';
import { successToast } from '../../../../../common/helpers/toast';
import ObjectUtils from '../../../../../common/helpers/utils/object-utils';
import { NeedHelpLabel } from '../../../common/components/NeedHelpLabel/NeedHelpLabel';
import { LiveChatContext } from '../../LiveChatContext';
import { LcConversationState } from '../../liveChatMachine';
import { Panes } from '../../LiveChatPanel';
import {
  buildConversationStatusMenuItems,
  getFormattedDate,
} from '../../shared';
import { ChannelIcon } from '../ChannelIcon';
import { ConversationAssigneeFilterDropdown } from '../ConversationAssigneeFilterDropdown/ConversationAssigneeFilterDropdown';
import { useConversationAssignmentOptions } from '../ConversationAssignmentDropdown/ConversationAssignmentDropdown';
import { LiveChatMenu } from '../LiveChatMenu/LiveChatMenu';
import { messageStructureParser } from '../Messages/messageStructureParser';
import { OnlineIndicator } from '../OnlineIndicator/OnlineIndicator';
import { ProfileImage } from '../ProfileImage/ProfileImage';
import { AdvancedFilters } from './AdvancedFilters/AdvancedFilters';
import { AgentAvailability } from './AgentAvailability/AgentAvailability';
import './Conversations.pcss';
import { EmptyState } from './EmptyState/EmptyState';
import { LcSearch } from './LcSearch/LcSearch';
import { PausedStatus } from './PausedStatus/PausedStatus';
import { SearchResults } from './SearchResults/SearchResults';
import { selectCurrentUserIsAdmin } from '@mm/state/selectors/userSelector';
import { ReactComponent as GmailIcon } from '../../../beta/modules/Automations/AutomationHome/TemplateIndex/gmail.svg';

const cache = new CellMeasurerCache({
  defaultHeight: 100,
  minHeight: 68,
  fixedWidth: true,
});

const useActivateConversation = (setActivePane: (pane: Panes) => void) => {
  const { isMobile } = useResolutionInfo();

  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );

  const [lcMachineStateTalkative] = useActor(lcService);
  const lcMachineState = useDebounce(lcMachineStateTalkative, 100);

  const lcMachineSend = lcService.send;

  const { onlineRemoteUserIds, remoteUserId: selectedRemoteUserId } =
    lcMachineState.context || {};

  const activeBotDefaultPageId = useSelector((state: StoreState) =>
    defaultPageIdSelector(state)?.toString(),
  );

  const activateConversation = useCallback(
    (
      conversation: Pick<LivechatConversationModel, 'remoteUserId'> &
        Partial<LivechatConversationModel>,
    ) => {
      if (isMobile) {
        return;
      }
      if (conversation.remoteUserId !== selectedRemoteUserId) {
        lcMachineSend({
          type: 'LC_REMOTE_USER_ID_CHANGE',
          payload: conversation.remoteUserId,
        });
        setActivePane(Panes.messages);
      }
    },
    [lcMachineSend, selectedRemoteUserId],
  );

  const isFollowUp = useCallback(
    (conversation: Pick<LivechatConversationModel, 'currentPageId'>) => {
      const isDefaultReply =
        conversation.currentPageId === activeBotDefaultPageId;

      return isDefaultReply;
    },
    [activeBotDefaultPageId],
  );

  const isOnline = useCallback(
    (conversation: Pick<LivechatConversationModel, 'remoteUserId'>) =>
      onlineRemoteUserIds.has(conversation.remoteUserId),
    [JSON.stringify(onlineRemoteUserIds)],
  );

  return { activateConversation, isFollowUp, isOnline };
};

const ConversationText = (props: {
  conversation: Pick<
    LivechatConversationModel,
    | 'structure'
    | 'time'
    | 'remoteUserId'
    | 'currentPageId'
    | 'muted'
    | 'mutedTill'
  >;
  subConversationMode?: boolean;
  setActivePane: (pane: Panes) => void;
}) => {
  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );

  const [lcMachineStateTalkative] = useActor(lcService);
  const lcMachineState = useDebounce(lcMachineStateTalkative, 100);

  const { remoteUserId: selectedRemoteUserId } = lcMachineState.context || {};

  const { conversation, subConversationMode, setActivePane } = props;

  const { activateConversation, isFollowUp, isOnline } =
    useActivateConversation(setActivePane);

  if (!conversation) {
    return null;
  }
  const { structure, remoteUserId } = conversation;
  const parsedStructure = JSONUtils.safeParse(structure);

  if (!parsedStructure) {
    return null;
  }
  const { getDisplayText } = messageStructureParser({
    structure: ObjectUtils.deepCamelizeKeys(parsedStructure),
  });

  const parsedMessage = getDisplayText();

  const content = (
    <div className="live-chat--conversation__text">{parsedMessage}</div>
  );

  if (!subConversationMode) {
    return content;
  }
  const containerClassNames = classnames(
    'live-chat--conversation__sub-conversation',
    {
      'live-chat--conversation__sub-conversation--selected':
        selectedRemoteUserId === remoteUserId,
    },
  );
  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    activateConversation(conversation);
  };
  return (
    <div className={containerClassNames} onClick={onClick}>
      <Circle
        className="live-chat--conversation__sub-conversation-online-indicator"
        color={isOnline(conversation) ? '#6abaa4' : '#00000000'}
      />
      <ChannelIcon
        remoteUserId={remoteUserId}
        className="live-chat--conversation__sub-conversation-icon"
      />
      {content}
      <div className="live-chat--conversation__sub-conversation-timestamp">
        {getFormattedDate(Number(conversation.time))}
      </div>
      {isFollowUp(conversation) && (
        <Circle
          className="live-chat--conversation__sub-conversation-follow-up-indicator"
          color="#ea118d"
        />
      )}
      <PausedStatus conversation={conversation} />
    </div>
  );
};

type ConversationProps = {
  conversation: LivechatConversationModel;
  idx: number;
  conversationSelection: Set<string> | 'all' | undefined;
  onClickConversationSelector: (
    e: React.MouseEvent,
    remoteUserId: string,
    selectionIdx: number,
  ) => void;
  currentConversationState: LcConversationState;
  setActivePane: (pane: Panes) => void;
  style: any;
};

export const Conversation = ({
  conversation,
  idx,
  conversationSelection,
  onClickConversationSelector,
  currentConversationState,
  style,
  setActivePane,
}: ConversationProps) => {
  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );

  const [lcMachineStateTalkative] = useActor(lcService);
  const lcMachineState = useDebounce(lcMachineStateTalkative, 50);

  const { remoteUserId: selectedRemoteUserId } = lcMachineState.context || {};

  const { activateConversation, isFollowUp, isOnline } =
    useActivateConversation(setActivePane);
  const subConversationMode = !!conversation.linkedContacts?.length;

  return (
    <div
      className="live-chat--conversation-wrapper"
      key={conversation.remoteUserId}
      style={style}
    >
      <div
        className={
          conversation.remoteUserId === selectedRemoteUserId ||
          conversation.linkedContacts?.some(
            lc => lc.remoteUserId === selectedRemoteUserId,
          ) ||
          conversationSelection === 'all' ||
          conversationSelection?.has(conversation.remoteUserId)
            ? 'live-chat--conversation--selected'
            : 'live-chat--conversation'
        }
        // TODO: BE could potentially send same ids back due to sorting of data
        // Need to come up with a way to handle it.
        // key={c.id + Date.now() + Math.floor(Math.random() * 500)}
        onClick={() => activateConversation(conversation)}
      >
        <input
          type="checkbox"
          className="live-chat--left-pane__selection-checkbox live-chat--left-pane__conversation-selection-checkbox"
          checked={
            conversationSelection === 'all' ||
            !!conversationSelection?.has(conversation.remoteUserId)
          }
          style={{
            marginTop: subConversationMode ? '8px' : '12px',
          }}
          onClick={e =>
            onClickConversationSelector(e, conversation.remoteUserId, idx)
          }
        />

        <div className="live-chat-conversation__details">
          <div className="live-chat--conversation__title">
            <div className="live-chat--conversation__contact-container">
              <ProfileImage
                altText={conversation.preferredName || 'avatar'}
                profilePictureUrl={
                  conversation.profilePic ||
                  conversation.linkedContacts?.find(c => c.profilePic)
                    ?.profilePic
                }
              />
              <div className="live-chat--conversation__conversation-contact-info">
                {!subConversationMode && (
                  <div className="live-chat--conversation__contact-icons">
                    <ChannelIcon remoteUserId={conversation.remoteUserId} />
                    {isOnline(conversation) && <OnlineIndicator />}
                  </div>
                )}
                <div className="live-chat--conversation__contact">
                  {conversation.preferredName}
                  {(conversation.lastName === 'IG' ||
                    conversation.firstName === 'Instagram Contact') &&
                    !conversation.username && (
                      <Tooltip
                        placement="right"
                        title={
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 16 }}>
                              Why does this say "Instagram" or "IG" instead of a
                              name?
                            </div>
                            <ul className="live-chat-conversation__instagram-note-tooltip-list">
                              {' '}
                              One of the following two reasons:
                              <li className="live-chat-conversation__instagram-note-tooltip-list-item">
                                The Instagram user does not have a name on their
                                account (only a handle, which Customers.ai
                                cannot access)
                              </li>
                              <li className="live-chat-conversation__instagram-note-tooltip-list-item">
                                Their name is restricted due to EU Data Privacy
                                laws (this issue will be resolved by Facebook
                                soon)
                              </li>
                            </ul>
                          </div>
                        }
                      >
                        {' '}
                        <QuestionCircleIcon
                          className="live-chat--conversation__instagram-info"
                          color={colors.accentBlue}
                        />
                      </Tooltip>
                    )}
                </div>
              </div>
            </div>
            <div className="live-chat--conversation__last-updated">
              {!subConversationMode &&
                currentConversationState !== 'done' &&
                isFollowUp(conversation) && (
                  <div className="live-chat--conversation__follow-up" />
                )}
              {subConversationMode ? (
                getFormattedDate(Number(conversation.time))
              ) : (
                <PausedStatus conversation={conversation} showDateFallback />
              )}
            </div>
          </div>
          <ConversationText
            conversation={conversation}
            subConversationMode={subConversationMode}
            setActivePane={setActivePane}
          />
          {conversation.linkedContacts?.map(lc => (
            <ConversationText
              key={lc.remoteUserId}
              conversation={lc}
              subConversationMode
              setActivePane={setActivePane}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export const Conversations = ({
  setActivePane,
}: {
  setActivePane: (pane: Panes) => void;
}) => {
  const { isMobile } = useResolutionInfo();

  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );

  const [lcMachineStateTalkative] = useActor(lcService);
  const lcMachineState = useDebounce(lcMachineStateTalkative, 100);

  const [scrollTopAfterDelete, setScrollTopAfterDelete] = useState(0);
  const [latestManualScrollTop, setLatestManualScrollTop] = useState(0);
  const [prevConversationsLength, setPrevConversationsLength] = useState(0);
  const [showDelayWarning, setShowDelayWarning] = useState(false);
  const listRef = useRef<List>();

  // Note: The effect reduces the problem of jumping scroll that is triggered
  // by react-virtualized List component on elements delition
  useEffect(() => {
    // First scroll is done to make the scroll almost unnoticable
    const handle = setTimeout(() => {
      listRef.current?.scrollToPosition(scrollTopAfterDelete);
    }, 30);

    // Second one is a safecguard if first one fired too early due to computer slownness or other reasons.
    const handleTwo = setTimeout(() => {
      listRef.current?.scrollToPosition(scrollTopAfterDelete);
    }, 100);

    return () => {
      clearTimeout(handle);
      clearTimeout(handleTwo);
    };
  }, [scrollTopAfterDelete]);
  const lcMachineSend = lcService.send;

  const setSearchResultMatches = (matches: ConversationsSearchResultMatch[]) =>
    lcMachineSend({
      type: 'LC_CONTEXT_UPDATE',
      payload: {
        searchResultMatches: matches,
      },
    });

  const clearSearchResultsMessages = () =>
    lcMachineSend({
      type: 'LC_CONTEXT_UPDATE',
      payload: {
        searchResultsMessages: [],
      },
    });

  const { remoteFbPageId = '', remoteUserId: selectedRemoteUserId } =
    lcMachineState.context || {};
  const { conversationState: currentConversationState, selectedChannels } =
    lcMachineState.context;

  const isFetchingConversations = lcMachineState.matches({
    mainLogic: {
      active: {
        view: {
          active: {
            conversationsLoading: 'loading',
          },
        },
      },
    },
  });

  const [isSearchActive, setIsSearchActive] = useState(false);

  const [remoteUserIdBeforeSearch, setRemoteUserIdBeforeSearch] = useState<
    string | undefined
  >(undefined);

  // Note: 3rd compare function guards from excessive rerenders that freeze browser.
  // The machine is anyway quite 'talkative' and frequently renders updates.
  // We might want to move the processing state to lcMachine on updates
  // instead of constantly trying to collect it from the Context and Stae.
  const conversationsToShowRaw = useXstateSelector(
    lcService,
    state => {
      const allConversations: Record<string, LivechatConversationModel> = {};
      Object.entries(state.context.conversationsRefs).forEach(([k, v]) => {
        allConversations[k] = v.state?.context?.model;
      });

      return allConversations;
    },
    (prevConversations, nextConversations) =>
      JSON.stringify(prevConversations) === JSON.stringify(nextConversations),
  );

  let conversationsToShow: LivechatConversationModel[] = [];

  conversationsToShow = useMemo(() => {
    const state = lcMachineState;
    const allConversations = conversationsToShowRaw;

    let maybeFilteredConversations: LivechatConversationModel[] = [];

    if (state.context.conversationSubset === 'main') {
      const mostRecentConversations = [
        ...state.context.mostRecentConversationsRemoteUserIds,
      ]
        .map(remoteUserId => state.context.conversationsRefs[remoteUserId])
        .filter(actor => !!actor)
        .map(actor => {
          return actor.state?.context?.model;
        });

      maybeFilteredConversations = mostRecentConversations.filter(
        c => c && c.inboxStatus === 'main',
      );
    }

    if (state.context.conversationSubset === 'follow_up') {
      maybeFilteredConversations = Object.values(allConversations).filter(
        c => c && c.inboxStatus === 'main' && c.followUp,
      );
    }

    if (state.context.conversationSubset === 'filtered') {
      maybeFilteredConversations = [
        ...state.context.filteredConversationRemoteUserIds,
      ]
        .map(remoteUserId => allConversations[remoteUserId])
        .filter(c => !!c);
    }

    maybeFilteredConversations = maybeFilteredConversations.filter(c => {
      if (state.context.conversationState === 'follow_up') {
        return c.inboxStatus === 'main' && c.followUp;
      }
      return c.inboxStatus === state.context.conversationState;
    });

    maybeFilteredConversations = maybeFilteredConversations.filter(c => {
      if (
        state.context.selectedAssignee !== 'all' &&
        state.context.selectedAssignee !== 'unassigned'
      ) {
        return Number(c.assignedUserId) === state.context.selectedAssignee;
      }
      return true;
    });

    const sortedConversations = maybeFilteredConversations.sort((a, b) => {
      return b.time !== a.time
        ? Number(b.time || 0) - Number(a.time || 0)
        : Number(b.conversationId) - Number(a.conversationId);
    });
    const remoteIdsSeen = new Set<string>();
    const newConversationsToShow = sortedConversations
      .filter(c => {
        if (remoteIdsSeen.has(c.remoteUserId)) {
          return false;
        }
        remoteIdsSeen.add(c.remoteUserId);
        c.linkedContacts?.forEach(lc => remoteIdsSeen.add(lc.remoteUserId));
        return true;
      })
      .map(c => ({
        ...c,
        preferredName: preferredConversationName(c),
      }));

    // Note: we need to invalidate CellMeasurer cache on changes to the content of the conversations,
    // which e.g. happens when changing filters
    // eslint-disable-next-line no-underscore-dangle
    const cacheRowCount = cache._rowCount;
    if (cacheRowCount) {
      for (let i = 0; i < cacheRowCount; i += 1) {
        const newConversation = newConversationsToShow?.[i];
        const oldConversation = conversationsToShow?.[i];
        // Note: I guess it might be more lightweight just to compare remoteUserIds
        // But it will skip updates similar to new linked contacts
        if (!newConversation || !isEqual(newConversation, oldConversation)) {
          cache.clear(i, 0);
        }
      }
    }
    // Handling the case with conversation being moved/deleted which triggers scroll to bottom
    if (newConversationsToShow.length < prevConversationsLength) {
      setScrollTopAfterDelete(latestManualScrollTop);
    }

    setPrevConversationsLength(newConversationsToShow.length);
    return newConversationsToShow as LivechatConversationModel[];
  }, [
    JSON.stringify(conversationsToShowRaw),
    JSON.stringify(lcMachineState.context.mostRecentConversationsRemoteUserIds),
    JSON.stringify(lcMachineState.context.filteredConversationRemoteUserIds),
    JSON.stringify(Object.keys(lcMachineState.context.conversationsRefs)),
    lcMachineState.context.conversationSubset,
    lcMachineState.context.conversationState,
    isFetchingConversations,
  ]);

  const [conversationSelection, setConversationSelection] = useState<
    Set<string> | 'all'
  >();
  const [isAllLoadedSelected, setIsAllLoadedSelected] = useState(false);

  const [conversationSelectionFocusIndex, setConverstionSelectionFocusIndex] =
    useState(-1);
  const selectAllConversationsCheckboxRef = useRef<HTMLInputElement | null>(
    null,
  );

  const resetSelection = () => {
    setConversationSelection(undefined);
    setConverstionSelectionFocusIndex(-1);
  };

  useEffect(() => {
    if (!selectAllConversationsCheckboxRef.current) {
      return;
    }
    selectAllConversationsCheckboxRef.current.indeterminate =
      conversationSelection instanceof Set;
  }, [selectAllConversationsCheckboxRef, conversationSelection]);

  const onClickSelectAllConversations = useCallback(() => {
    const newConversationSelection = conversationSelection
      ? undefined
      : new Set(conversationsToShow.map(c => c.remoteUserId));

    setConversationSelection(newConversationSelection);
    if (newConversationSelection) {
      setIsAllLoadedSelected(true);
    } else {
      setIsAllLoadedSelected(false);
    }
  }, [
    setConversationSelection,
    JSON.stringify(conversationSelection),
    JSON.stringify(conversationsToShow),
  ]);

  const onClickSelectLoadedConversations = useCallback(() => {
    setConversationSelection(
      new Set(conversationsToShow.map(c => c.remoteUserId)),
    );
    setIsAllLoadedSelected(true);
  }, [JSON.stringify(conversationsToShow)]);

  const onClickConversationSelector = useCallback(
    (e: React.MouseEvent, remoteUserId: string, selectionIdx: number) => {
      if (conversationSelection === 'all') {
        setConversationSelection(
          new Set(
            conversationsToShow
              .filter(c => c.remoteUserId !== remoteUserId)
              .map(c => c.remoteUserId),
          ),
        );
        return;
      }
      e.stopPropagation();
      const updatedSet = new Set([...(conversationSelection || [])]);

      if (
        e.shiftKey &&
        conversationSelectionFocusIndex >= 0 &&
        conversationSelectionFocusIndex !== selectionIdx
      ) {
        const focusedItemIsSelected = updatedSet.has(
          conversationsToShow[conversationSelectionFocusIndex].remoteUserId,
        );
        const start = Math.min(selectionIdx, conversationSelectionFocusIndex);
        const rangeEnd = Math.max(
          selectionIdx,
          conversationSelectionFocusIndex,
        );

        conversationsToShow.slice(start, rangeEnd + 1).forEach(c => {
          if (focusedItemIsSelected) {
            updatedSet.add(c.remoteUserId);
          } else {
            updatedSet.delete(c.remoteUserId);
          }
        });
      } else {
        if (updatedSet.has(remoteUserId)) {
          updatedSet.delete(remoteUserId);
        } else {
          updatedSet.add(remoteUserId);
        }
        setConverstionSelectionFocusIndex(selectionIdx);
      }
      setConversationSelection(updatedSet.size ? updatedSet : undefined);
    },
    [JSON.stringify(conversationsToShow), conversationSelection],
  );

  const { conversationSubset } = lcMachineState.context;

  const moreToFetch =
    lcMachineState.context.fetchStatus[conversationSubset]?.moreToFetch;

  const onCloseSearch = () => {
    setIsSearchActive(false);
    lcMachineSend({
      type: 'LC_CONTEXT_UPDATE',
      payload: {
        searchString: '',
        foundContacts: [],
        foundConversations: undefined,
      },
    });
    setSearchResultMatches([]);
    clearSearchResultsMessages();

    lcMachineSend({
      type: 'LC_REMOTE_USER_ID_CHANGE',
      payload: remoteUserIdBeforeSearch,
    });
    setRemoteUserIdBeforeSearch(undefined);
  };

  const handleFilterOptionChange = useCallback(
    async (option: LivechatConversationStateOption) => {
      setShowDelayWarning(false);
      const viewUpdate = {
        conversationState: option.kind,
      };

      lcMachineSend({
        type: 'LC_VIEW_UPDATE',
        payload: { viewUpdate },
      });

      if (selectedRemoteUserId) {
        lcMachineSend({ type: 'LC_REMOTE_USER_ID_CHANGE', payload: undefined });
        setIsInitialRemoteUserIdSet(false);
      }
    },
    [lcMachineSend, selectedRemoteUserId],
  );

  const [isInitialRemoteUserIdSet, setIsInitialRemoteUserIdSet] = useState(
    !!selectedRemoteUserId,
  );

  useEffect(() => {
    if (!selectedRemoteUserId) {
      setIsInitialRemoteUserIdSet(false);
    }
  }, [
    lcMachineState.context.conversationSubset,
    lcMachineState.context.conversationState,
    lcMachineState.context.selectedAssignee,
    JSON.stringify(lcMachineState.context.selectedAttribute),
    JSON.stringify(lcMachineState.context.selectedChannels),
    JSON.stringify(lcMachineState.context.selectedTags),
  ]);

  useEffect(() => {
    if (!isInitialRemoteUserIdSet && conversationsToShow.length && !isMobile) {
      setIsInitialRemoteUserIdSet(true);

      lcMachineSend({
        type: 'LC_REMOTE_USER_ID_CHANGE',
        payload: conversationsToShow[0].remoteUserId,
      });
      setActivePane(Panes.messages);
    }
  }, [JSON.stringify(conversationsToShow)]);

  const pageHasNoConversations =
    Object.keys(lcMachineState?.context.conversationsRefs).length === 0;
  const noConversationsToShow =
    !pageHasNoConversations && !conversationsToShow.length;

  const showEmptyState =
    !isFetchingConversations &&
    !moreToFetch &&
    (pageHasNoConversations || (!conversationsToShow.length && !moreToFetch));

  const isAdvancedFiltersActive = !!(
    !!lcMachineState?.context.selectedAttribute ||
    lcMachineState?.context.selectedTags.length
  );

  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(
    isAdvancedFiltersActive,
  );

  const ChannelFilterButton = (props: {
    channel: LiveChatChannel;
    channelIcon: any;
    title: string;
    selectedChannels: LiveChatChannel[];
  }) => {
    const { channel, channelIcon, title, selectedChannels } = props;
    const channelSelected = selectedChannels?.includes(channel);

    const onClick = () => {
      // Note: triggering another update while this one is still in progress
      // occasionally leads to never-ending loading indicator.
      // We should not allow an update before previous one finished.
      const updatedChannels = channelSelected
        ? selectedChannels.filter(c => c !== channel)
        : [...selectedChannels, channel];

      const viewUpdate = {
        selectedChannels: updatedChannels,
      };

      lcMachineSend({
        type: 'LC_VIEW_UPDATE',
        payload: { viewUpdate },
      });

      if (
        selectedRemoteUserId &&
        updatedChannels.length &&
        !updatedChannels.includes(identifyChannel(selectedRemoteUserId))
      ) {
        setIsInitialRemoteUserIdSet(false);
      }
    };
    return (
      <div
        className={classnames('live-chat--left-pane__channel-filter-button', {
          'live-chat--left-pane__channel-filter-button--selected':
            channelSelected,
        })}
        onClick={onClick}
      >
        {React.createElement(channelIcon)}
        <div className="live-chat--left-pane__channel-filter-button-tooltip">
          {title}
        </div>
      </div>
    );
  };

  const ConversationSelectionActionRow = () => {
    const { updateConversationStatus, deleteConversations } =
      useLiveChatCRUDActions();
    const assignToMenuItems = useConversationAssignmentOptions();
    const { openGeneralConfirmModal } = useModalActions();
    const isMMAdmin = useSelector(selectCurrentUserIsAdmin);

    const moveItems = async (
      updatedInboxStatus: LiveChatInboxStatus,
      displayName: string,
    ) => {
      const filterParams = {
        conversationState: lcMachineState.context.conversationState,
        assignedUserId: lcMachineState.context.selectedAssignee,
        selectedChannels: lcMachineState.context.selectedChannels,
        selectedTags: lcMachineState.context.selectedTags,
      };

      setIsAllLoadedSelected(false);

      const params =
        conversationSelection instanceof Set
          ? {
              remotePageId: remoteFbPageId,
              remoteUserIds: [...conversationSelection],
              inboxStatus: updatedInboxStatus,
            }
          : {
              remotePageId: remoteFbPageId,
              remoteUserIds: 'all' as const,
              inboxStatus: updatedInboxStatus,
              filterParams,
            };

      const result = await updateConversationStatus(params);

      const needsReset =
        conversationSelection instanceof Set
          ? conversationSelection.size > 10
          : true;

      if (
        (updatedInboxStatus === 'done' ||
          currentConversationState === 'done') &&
        (conversationSelection === 'all' ||
          Number(conversationSelection?.size) > 10)
      ) {
        setShowDelayWarning(true);
      }

      if (needsReset) {
        lcMachineSend({
          type: 'LC_REMOTE_USER_ID_CHANGE',
          payload: undefined,
        });
        resetSelection();
        return setTimeout(
          () =>
            lcMachineSend({
              type: 'LC_VIEW_BULK_RESET',
            }),
          300,
        );
      }

      lcMachineSend({
        type: 'LC_CONVERSATIONS_REFRESH',
        payload: params.remoteUserIds,
      });

      lcMachineSend({
        type: 'LC_CONVERSATIONS_REFILTER',
        payload: params.remoteUserIds,
      });

      resetSelection();
      if (result?.conversationsUpdated) {
        successToast(
          `${result.conversationsUpdated} contacts have been moved to ${displayName}`,
          'bottom-left',
        );
      }
    };
    const moveToMenuItems = buildConversationStatusMenuItems(
      currentConversationState === 'follow_up'
        ? 'main'
        : currentConversationState,
      () => undefined,
      true,
    );

    const assignItems = async (assignedUserId: string, agentName: string) => {
      const filterParams = {
        conversationState: lcMachineState.context.conversationState,
        assignedUserId: lcMachineState.context.selectedAssignee,
        selectedChannels: lcMachineState.context.selectedChannels,
        selectedTags: lcMachineState.context.selectedTags,
      };

      setIsAllLoadedSelected(false);

      const params =
        conversationSelection instanceof Set
          ? {
              remotePageId: remoteFbPageId,
              remoteUserIds: [...conversationSelection],
              assignedUserId,
            }
          : {
              remotePageId: remoteFbPageId,
              remoteUserIds: 'all' as const,
              assignedUserId,
              filterParams,
            };

      const result = await updateConversationStatus(params);

      const needsReset =
        conversationSelection instanceof Set
          ? conversationSelection.size > 10
          : true;

      if (needsReset) {
        resetSelection();
        return lcMachineSend({
          type: 'LC_VIEW_BULK_RESET',
        });
      }

      lcMachineSend({
        type: 'LC_CONVERSATIONS_REFRESH',
        payload: params.remoteUserIds,
      });

      Object.entries(lcMachineState.context.contactsRefs)
        .filter(([remoteUserId, actor]) => {
          if (conversationSelection instanceof Set) {
            return conversationSelection.has(remoteUserId);
          }
          if (!conversationSelection) return false;
          return true;
        })
        .forEach(([remoteUserId, actor]) => {
          actor.send({
            type: 'LC_CONTACT_REFRESH',
            payload: remoteUserId || '',
          });
        });

      resetSelection();
      if (result?.conversationsUpdated) {
        const activeUserId = assignToMenuItems[0].id;
        const assignedTo = (() => {
          switch (assignedUserId) {
            case '':
              return 'unassigned';
            case activeUserId:
              return 'assigned to you';
            default:
              return `assigned to ${agentName}`;
          }
        })();
        successToast(
          `${result.conversationsUpdated} contacts have been ${assignedTo}`,
          'bottom-left',
        );
      }
    };

    const deleteItems = async () => {
      await openGeneralConfirmModal({
        headerText: 'Permanently delete contacts?',
        okButtonText: 'Delete',
        okButtonStyling: { red: true },
        cancelButtonFirst: true,
        messageText:
          'Clicking DELETE will permanently destroy all records of these contacts, including past conversations and attributes, ' +
          'that are stored on MobileMonkey’s servers. There is no way to undo this action.',
        onCloseHandler: async ok => {
          if (ok) {
            const remoteUserIds =
              conversationSelection instanceof Set
                ? [...conversationSelection]
                : 'all';
            const filterParams =
              remoteUserIds !== 'all'
                ? undefined
                : {
                    conversationState: lcMachineState.context.conversationState,
                    selectedAssignee: lcMachineState.context.selectedAssignee,
                    selectedChannels: lcMachineState.context.selectedChannels,
                  };

            const result = await deleteConversations({
              remotePageId: remoteFbPageId,
              remoteUserIds,
              filterParams,
            });
            if (result?.success) {
              lcMachineSend({
                type: 'LC_CONVERSATIONS_DELETE',
                payload: remoteUserIds,
              });
            }
            resetSelection();
            lcMachineSend({
              type: 'LC_REMOTE_USER_ID_CHANGE',
              payload: undefined,
            });
            if (result?.contactsDeleted) {
              successToast(
                `${result.contactsDeleted} contacts have been deleted`,
                'bottom-left',
              );
            }
          }
        },
      });
    };

    return !conversationSelection ? null : (
      <div className="live-chat--left-pane__header live-chat--left-pane__selection-action-row">
        <div className="live-chat--left-pane__selection-action-row-item">
          <Heading
            size={6}
            className="live-chat--left-pane__selection-action-row-header"
          >
            Move to:
          </Heading>

          <TypedPrimarySelect
            className="live-chat--left-pane__selection-action-row-selector"
            activeItemClassName="live-chat--left-pane__selection-action-row-selector-item"
            activeItemNameClassName="live-chat--left-pane__selection-action-row-selector-item-name"
            dropdownClassName="live-chat--left-pane__selection-action-row-selector-dropdown"
            options={moveToMenuItems.map(item => ({
              ...item,
              id: item.inboxStatus,
            }))}
            value={{ id: '' as any as LiveChatInboxStatus, name: '' }}
            onChange={item => moveItems(item.id, item.name)}
            customArrowIcon={<AngleDownLightIcon color="#ffffff" />}
            customOptionRenderer={(option, activeItem) => {
              if (activeItem) {
                return <>Select</>;
              }
              return <>{option.name}</>;
            }}
          />
        </div>
        <div className="live-chat--left-pane__selection-action-row-item">
          <Heading
            size={6}
            className="live-chat--left-pane__selection-action-row-header"
          >
            Assign to:
          </Heading>
          <TypedPrimarySelect
            className="live-chat--left-pane__selection-action-row-selector"
            activeItemClassName="live-chat--left-pane__selection-action-row-selector-item"
            activeItemNameClassName="live-chat--left-pane__selection-action-row-selector-item-name"
            dropdownClassName="live-chat--left-pane__selection-action-row-selector-dropdown"
            options={assignToMenuItems}
            value={{ id: '-1', name: 'Dummy Value' }}
            onChange={item => assignItems(item.id, item.name)}
            customArrowIcon={<AngleDownLightIcon color="#ffffff" />}
            customOptionRenderer={(option, activeItem) => {
              if (activeItem) {
                return <>Select</>;
              }
              return <>{option.name}</>;
            }}
          />
        </div>
        {isMMAdmin && <DeleteButton onClick={deleteItems} />}
      </div>
    );
  };

  const renderConversationSelection = useMemo(() => {
    return conversationSelection ? (
      <ConversationSelectionActionRow />
    ) : (
      <div className="live-chat--left-pane__header">
        <PrimarySelect
          className={classnames('live-chat--left-pane__filter-select', {
            'live-chat--left-pane__filter-select--active':
              currentConversationState !== 'main',
          })}
          disabled={isSearchActive}
          dropdownClassName="live-chat--left-pane__filter-dropdown"
          activeItemClassName="live-chat--left-pane__filter-active-item"
          activeItemNameClassName="live-chat--left-pane__filter-active-item-name"
          options={LivechatConversationStateOptions}
          value={
            LivechatConversationStateOptions.find(
              o => o.kind === currentConversationState,
            ) || LivechatConversationStateOptions[0]
          }
          onChange={handleFilterOptionChange}
          customArrowIcon={<AngleDownLightIcon />}
        />
        <div style={{ flexGrow: 1 }}>&nbsp;</div>
        <ConversationAssigneeFilterDropdown />
      </div>
    );
  }, [
    handleFilterOptionChange,
    JSON.stringify(conversationSelection),
    isSearchActive,
    JSON.stringify(currentConversationState),
    lcMachineState.context.selectedChannels,
    lcMachineState.context.selectedAttribute,
    lcMachineState.context.selectedTags,
    lcMachineState.context.selectedAssignee,
  ]);

  const renderConversationsFilterButtons = useMemo(() => {
    return (
      <div className="live-chat--left-pane__header">
        <input
          type="checkbox"
          className="live-chat--left-pane__selection-checkbox"
          ref={selectAllConversationsCheckboxRef}
          checked={!!conversationSelection}
          onClick={onClickSelectAllConversations}
        />
        {(isAllLoadedSelected || conversationSelection === 'all') &&
        isAllLoadedSelected ? (
          <div>
            <div className="live-chat--left-pane__all-convos-selected-warning">
              <b>
                {conversationSelection instanceof Set &&
                  conversationSelection?.size}
              </b>{' '}
              loaded conversations selected
            </div>
            <div
              className="live-chat--left-pane__all-convos-selected-action"
              onClick={() => {
                setConversationSelection('all');
                setIsAllLoadedSelected(false);
              }}
            >
              Select all conversations
            </div>
          </div>
        ) : conversationSelection === 'all' ? (
          <div>
            <div className="live-chat--left-pane__all-convos-selected-warning">
              ALL conversations selected
            </div>
            <div
              className="live-chat--left-pane__all-convos-selected-action"
              onClick={onClickSelectLoadedConversations}
            >
              Select loaded conversations only
            </div>
          </div>
        ) : null}
        <div style={{ flexGrow: 1 }}>&nbsp;</div>
        <ChannelFilterButton
          channel="email"
          channelIcon={GmailIcon}
          title="Email"
          selectedChannels={selectedChannels}
        />
        <ChannelFilterButton
          channel="instagram"
          channelIcon={InstagramIcon}
          title="Instagram"
          selectedChannels={selectedChannels}
        />
        <ChannelFilterButton
          channel="facebook"
          channelIcon={FBIcon}
          title="Messenger"
          selectedChannels={selectedChannels}
        />
        <ChannelFilterButton
          channel="sms"
          channelIcon={SMSIcon}
          title="SMS"
          selectedChannels={selectedChannels}
        />
        <ChannelFilterButton
          channel="webchat"
          channelIcon={WebchatIcon}
          title="Webchat"
          selectedChannels={selectedChannels}
        />
      </div>
    );
  }, [
    JSON.stringify(selectedChannels),
    selectAllConversationsCheckboxRef,
    !!conversationSelection,
    onClickSelectAllConversations,
    isFetchingConversations,
  ]);

  const rowRenderer = ({ key, index, parent, style }) => {
    const conversation = conversationsToShow[index];
    return (
      <CellMeasurer
        cache={cache}
        columnIndex={0}
        key={key}
        parent={parent}
        rowIndex={index}
      >
        {conversation ? (
          <Conversation
            style={style}
            conversation={conversation}
            idx={index}
            conversationSelection={conversationSelection}
            onClickConversationSelector={onClickConversationSelector}
            currentConversationState={currentConversationState}
            setActivePane={setActivePane}
          />
        ) : null}
      </CellMeasurer>
    );
  };

  if (isSearchActive) {
    return (
      <div className="live-chat--left-pane">
        <div className="live-chat--left-pane__search-header live-chat--left-pane__header">
          <LcSearch />
          <CloseCircleIcon
            className="live-chat--close-search--icon"
            onClick={onCloseSearch}
          />
        </div>
        <SearchResults />
      </div>
    );
  }

  return (
    <div className="live-chat--left-pane">
      <div className="live-chat--left-pane__top-header">
        <AgentAvailability />

        <div className="live-chat--left-pane__top-header-text">
          Live Chat
          <NeedHelpLabel
            link="https://customers.ai/help/article/n4gjmz2dz7-inbox"
            iconOnly
          />
        </div>

        <div
          className="live-chat--left-pane__top-header-button"
          onClick={() => {
            setIsSearchActive(true);
            setRemoteUserIdBeforeSearch(selectedRemoteUserId);
          }}
        >
          <SearchIcon className="live-chat--left-pane__top-header-icon" />
        </div>

        <div
          className={classnames('live-chat--left-pane__top-header-button', {
            'live-chat--left-pane__top-header-advanced-filter-button-active':
              isAdvancedFiltersOpen,
          })}
          onClick={() => setIsAdvancedFiltersOpen(!isAdvancedFiltersOpen)}
        >
          {isAdvancedFiltersOpen || isAdvancedFiltersActive ? (
            <FilterFilledIcon className="live-chat--left-pane__top-header-icon" />
          ) : (
            <FilterIcon className="live-chat--left-pane__top-header-icon" />
          )}
        </div>

        <LiveChatMenu />
      </div>
      {isAdvancedFiltersOpen && <AdvancedFilters />}

      {renderConversationSelection}

      {renderConversationsFilterButtons}

      {showDelayWarning && (
        <div className="live-chat--conversations--delay-warning">
          <div className="live-chat--conversations--delay-warning-icon">
            <ExclamationIcon />
          </div>
          <div className="live-chat--conversations--delay-warning-text">
            Moving all conversations to/from Done takes a few moments. Check
            back soon. Reload the page to see updated results.
          </div>
          <div className="live-chat--conversations--delay-warning-button">
            <PrimaryButton
              onClick={() => {
                setShowDelayWarning(false);
                lcMachineSend({
                  type: 'LC_CONVERSATIONS_REFRESH',
                  payload: 'all',
                });
              }}
            >
              OK
            </PrimaryButton>
          </div>
        </div>
      )}
      {showEmptyState && (
        <EmptyState noConversationsToShow={noConversationsToShow} />
      )}
      <div className="live-chat--conversations">
        <InfiniteLoader
          isRowLoaded={({ index }) => {
            return !moreToFetch || index < conversationsToShow.length;
          }}
          loadMoreRows={() => {
            lcMachineSend?.({ type: 'LC_CONVERSATIONS_GET_MORE' });
          }}
          rowCount={
            moreToFetch
              ? conversationsToShow.length + 1 * 20
              : conversationsToShow.length
          }
          threshold={15}
        >
          {({ onRowsRendered, registerChild }) => (
            <AutoSizer>
              {({ height, width }) => (
                <List
                  ref={ref => {
                    listRef.current = ref;
                    registerChild(ref);
                  }}
                  onRowsRendered={onRowsRendered}
                  height={height}
                  rowCount={conversationsToShow.length}
                  rowHeight={cache.rowHeight}
                  rowRenderer={rowRenderer}
                  width={width}
                  overscanRowCount={4}
                  onScroll={props => {
                    setLatestManualScrollTop(props.scrollTop);
                  }}
                  // scrollToIndex={10}
                />
              )}
            </AutoSizer>
          )}
        </InfiniteLoader>
      </div>

      <div className="live-chat--conversations--status">
        {isFetchingConversations
          ? 'Loading conversations...'
          : 'Conversations Loaded'}
      </div>
    </div>
  );
};
